import { Badge } from '@repo/ui/components/ui/badge';
import { cn } from '@repo/ui/lib/utils';
import {
  AuditAction,
  AuditSeverity,
  AuditCategory,
} from '@repo/ui/lib/api/types/audit-log.types';
import {
  Shield,
  AlertTriangle,
  Info,
  XCircle,
  CheckCircle,
  Edit,
  Trash2,
  Eye,
  Plus,
  LogIn,
  LogOut,
  CreditCard,
  FileText,
  Settings,
  Lock,
  Unlock,
} from 'lucide-react';

/**
 * Configuration for severity badges
 */
const severityConfig = {
  [AuditSeverity.INFO]: {
    variant: 'default' as const,
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    icon: Info,
  },
  [AuditSeverity.WARNING]: {
    variant: 'default' as const,
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    icon: AlertTriangle,
  },
  [AuditSeverity.ERROR]: {
    variant: 'destructive' as const,
    className: '',
    icon: XCircle,
  },
  [AuditSeverity.CRITICAL]: {
    variant: 'default' as const,
    className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    icon: Shield,
  },
};

/**
 * Configuration for action badges
 */
const actionConfig: Record<string, { variant: any; className: string; icon: any }> = {
  [AuditAction.CREATE]: {
    variant: 'default',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    icon: Plus,
  },
  [AuditAction.UPDATE]: {
    variant: 'default',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    icon: Edit,
  },
  [AuditAction.DELETE]: {
    variant: 'destructive',
    className: '',
    icon: Trash2,
  },
  [AuditAction.READ]: {
    variant: 'outline',
    className: '',
    icon: Eye,
  },
  [AuditAction.LOGIN]: {
    variant: 'default',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    icon: LogIn,
  },
  [AuditAction.LOGOUT]: {
    variant: 'outline',
    className: '',
    icon: LogOut,
  },
  [AuditAction.LOGIN_FAILED]: {
    variant: 'destructive',
    className: '',
    icon: Lock,
  },
  [AuditAction.PAYMENT_SUCCESS]: {
    variant: 'default',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    icon: CreditCard,
  },
  [AuditAction.PAYMENT_FAILED]: {
    variant: 'destructive',
    className: '',
    icon: CreditCard,
  },
  [AuditAction.UNAUTHORIZED_ACCESS]: {
    variant: 'destructive',
    className: '',
    icon: Shield,
  },
  [AuditAction.PERMISSION_DENIED]: {
    variant: 'destructive',
    className: '',
    icon: Lock,
  },
  [AuditAction.ACCOUNT_LOCKED]: {
    variant: 'default',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    icon: Lock,
  },
  [AuditAction.ACCOUNT_UNLOCKED]: {
    variant: 'default',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    icon: Unlock,
  },
};

/**
 * Configuration for category badges
 */
const categoryConfig = {
  [AuditCategory.USER_ACTION]: {
    variant: 'default' as const,
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  [AuditCategory.SYSTEM]: {
    variant: 'outline' as const,
    className: 'text-gray-600 dark:text-gray-400',
  },
  [AuditCategory.SECURITY]: {
    variant: 'default' as const,
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  [AuditCategory.PERFORMANCE]: {
    variant: 'default' as const,
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  [AuditCategory.PAYMENT]: {
    variant: 'default' as const,
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  [AuditCategory.DATA]: {
    variant: 'default' as const,
    className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  },
};

/**
 * Badge component for severity levels
 */
export function SeverityBadge({ 
  severity, 
  showIcon = false 
}: { 
  severity: AuditSeverity; 
  showIcon?: boolean;
}) {
  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={cn(config.className, 'gap-1')}>
      {showIcon && <Icon className="h-3 w-3" />}
      {severity}
    </Badge>
  );
}

/**
 * Badge component for action types
 */
export function ActionBadge({ 
  action, 
  showIcon = false 
}: { 
  action: AuditAction; 
  showIcon?: boolean;
}) {
  const config = actionConfig[action] || {
    variant: 'outline',
    className: '',
    icon: FileText,
  };
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={cn(config.className, 'gap-1')}>
      {showIcon && <Icon className="h-3 w-3" />}
      {action.replace(/_/g, ' ')}
    </Badge>
  );
}

/**
 * Badge component for category types
 */
export function CategoryBadge({ 
  category 
}: { 
  category: AuditCategory;
}) {
  const config = categoryConfig[category];

  return (
    <Badge variant={config.variant} className={config.className}>
      {category.replace(/_/g, ' ')}
    </Badge>
  );
}

/**
 * Badge component for HTTP status codes
 */
export function StatusCodeBadge({ 
  statusCode 
}: { 
  statusCode: number | null;
}) {
  if (!statusCode) return null;

  let variant: 'default' | 'destructive' | 'outline' = 'default';
  let className = '';

  if (statusCode >= 200 && statusCode < 300) {
    variant = 'default';
    className = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  } else if (statusCode >= 300 && statusCode < 400) {
    variant = 'default';
    className = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  } else if (statusCode >= 400 && statusCode < 500) {
    variant = 'default';
    className = 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
  } else if (statusCode >= 500) {
    variant = 'destructive';
  }

  return (
    <Badge variant={variant} className={cn(className, 'font-mono')}>
      {statusCode}
    </Badge>
  );
}
